/* Admin · Compliance Documents (Feb 2026).
   Lets an admin upload real SOC 2 / pen-test / BCP / cyber insurance PDFs
   that flip the cards in the Banking Trust portal from "Placeholder" to
   "Available". */
import { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  ScrollText, Upload, FileText, RefreshCw, AlertTriangle, CheckCircle2, Trash2,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";

function fmtTime(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch { return s; }
}

export default function AdminComplianceDocs() {
  const [docs, setDocs] = useState([]);
  const [subProc, setSubProc] = useState([]);
  const [openUpload, setOpenUpload] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/compliance-docs");
      setDocs(data.rows || []);
      setSubProc(data.sub_processors || []);
    } catch (e) {
      toast.error("Could not load compliance docs");
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-compliance-docs" className="space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="display text-3xl tracking-tight flex items-center gap-2.5">
            <ScrollText className="w-6 h-6 text-sky-500" /> Compliance Documents
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Upload real SOC 2 / pen-test / BCP / cyber-insurance PDFs. Each upload flips the corresponding card
            in the Banking Trust portal from <span className="font-medium">Placeholder</span> to <span className="font-medium">Available</span>.
          </p>
        </div>
        <Button variant="outline" onClick={load} data-testid="compl-docs-refresh">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </header>

      <div className="rounded-2xl border bg-white overflow-hidden" data-testid="compl-docs-list">
        {docs.map(d => (
          <div key={d.doc_id} data-testid={`compl-doc-${d.doc_id}`} className="px-4 py-3 border-b border-slate-100 last:border-b-0 flex items-center justify-between gap-3">
            <div className="flex items-start gap-2.5 min-w-0">
              <FileText className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{d.title}</div>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  {d.is_placeholder ? (
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">
                      <AlertTriangle className="w-3 h-3 mr-1" /> Placeholder
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Available
                    </Badge>
                  )}
                  {d.nda_required && <span className="text-[10px] text-amber-600">NDA required</span>}
                  {d.target_q && d.is_placeholder && <span className="text-[10px] text-slate-500">target: {d.target_q}</span>}
                  {d.version && <span className="text-[10px] font-mono text-slate-500">v{d.version}</span>}
                  {d.uploaded_at && <span className="text-[10px] text-slate-500">uploaded {fmtTime(d.uploaded_at).split(",")[0]} by {d.uploaded_by}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button size="sm" onClick={() => setOpenUpload(d)} data-testid={`compl-doc-${d.doc_id}-upload`}>
                <Upload className="w-3.5 h-3.5 mr-1" /> {d.is_placeholder ? "Upload PDF" : "Replace"}
              </Button>
              {!d.is_placeholder && (
                <Button size="sm" variant="outline" onClick={() => revertDoc(d.doc_id, load)} data-testid={`compl-doc-${d.doc_id}-revert`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border bg-white overflow-hidden" data-testid="compl-subproc-list">
        <div className="px-4 py-2 bg-slate-50 text-sm font-medium text-slate-700 border-b">Sub-processors (read-only)</div>
        {subProc.map(s => (
          <div key={s.name} className="px-4 py-2.5 border-b border-slate-100 last:border-b-0 flex items-center justify-between">
            <div>
              <div className="text-sm text-slate-900">{s.name}</div>
              <div className="text-[11px] text-slate-500">{s.purpose}</div>
            </div>
            {s.soc_available && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">SOC 2</Badge>}
          </div>
        ))}
      </div>

      <UploadDialog doc={openUpload} onClose={() => setOpenUpload(null)} onDone={load} />
    </div>
  );
}

async function revertDoc(docId, reload) {
  if (!window.confirm(`Revert "${docId}" back to placeholder?`)) return;
  try {
    await api.delete(`/admin/compliance-docs/${docId}`);
    toast.success("Reverted to placeholder");
    reload();
  } catch (e) {
    toast.error("Revert failed");
  }
}

function UploadDialog({ doc, onClose, onDone }) {
  const [version, setVersion] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { if (doc) { setVersion(""); } }, [doc?.doc_id]);
  if (!doc) return null;

  const submit = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error("Select a PDF"); return; }
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("PDF only"); return; }
    if (!version.trim()) { toast.error("Version required"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("version", version.trim());
      await api.post(`/admin/compliance-docs/${doc.doc_id}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded — card is now Available");
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!doc} onOpenChange={onClose}>
      <DialogContent data-testid="compl-upload-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Upload · {doc.title}</DialogTitle>
          <DialogDescription>
            Replaces the placeholder PDF. Stamps the audit chain. Banks see this card flip to Available immediately.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="compl-version">Version</Label>
            <Input id="compl-version" data-testid="compl-upload-version" value={version} onChange={e => setVersion(e.target.value)} placeholder="2026-Q2" />
          </div>
          <div>
            <Label htmlFor="compl-file">PDF file (max 10 MB)</Label>
            <Input id="compl-file" data-testid="compl-upload-file" type="file" accept="application/pdf" ref={fileRef} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="compl-upload-submit" onClick={submit} disabled={busy}>
            {busy ? "Uploading…" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
